<script lang="ts">
import AlertDialog from '@/app/common/components/AlertDialog.vue';
import AttachUploader from '@/app/common/components/AttachUploader.vue';
import { ATTACH_TYPE, REQUEST_TYPE } from '@/app/const';
import Upload from '@/components/pages/opm/upload.vue';
import { BaseAlert } from '@/interface/BaseAlert.interface';
import { ComplaintInfo } from '@/interface/Complaint.interface';
import { fileService } from '@/services/serviceProvider';
import { ref } from 'vue';

export default {
    components: {
        AttachUploader,
        AlertDialog,
        Upload
    },
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        header: {
            type: String,
            default: 't-action-cancel-complaint'
        },
        cancelText: {
            type: String,
            default: 't-back'
        },
        confirmText: {
            type: String,
            default: 't-action-cancel-complaint'
        },
        complaint: {
            type: Object,
            default: {}
        }
    },
    emits: ['update:modelValue', 'onConfirm', 'onCancel'],
    setup() {
        const cancelReqForm = ref();
        const fileSv = fileService;

        return { cancelReqForm, fileSv };
    },
    data() {
        return {
            request: this.complaint as ComplaintInfo,
            attachments: [] as any[],
            displayUpload: false,
            failAlert: {
                display: false,
                success: false
            } as BaseAlert,
            rules: {
                required: (message: any) => {
                    return (v: any) => !!v || message;
                },
            }
        }
    },
    computed: {
        dialogShow: {
            get() {
                return this.modelValue || false
            },
            set(newValue: boolean) {
                this.$emit('update:modelValue', newValue);
            }
        },
        isOpmRequest() {
            return this.complaint.requestType === REQUEST_TYPE.OPM;
        },
        headers() {
            return [
                { title: this.$t('t-order'), sortable: false, key: 'index', align: 'center', width: '5%' },
                { title: this.$t('t-file-name'), key: 'fileName', width: '20%' },
                { title: this.$t('t-complaint-opm-file-description'), key: 'detail' },
                { title: this.$t('t-complaint-request-type'), key: 'docTypeIdText', width: '15%' },
                { title: '', key: 'docTypeText', width: '5%' },
                { title: '', key: 'actions', sortable: false, width: '2%' },
            ] as any[];
        },
    },
    methods: {
        async confirm() {
            const valid = await this.cancelReqForm.validate();
            if (valid.valid) {
                this.dialogShow = false;
                this.request.tempOpmAttachments = this.attachments;
                this.$emit('onConfirm', this.request);
            }
        },
        cancel() {
            this.request.ascEndReason = undefined;
            this.request.tempOpmAttachments = [];
            this.attachments = [];
            this.dialogShow = false;
            this.$emit('onCancel')
        },
        deleteAttachment(attach: any) {
            this.attachments = this.attachments?.filter(o => o.name !== attach.name);
        },
        updateAttachments(value: any[]) {
            this.attachments = value;
        },
    }
}
</script>

<template>
    <v-dialog v-model="dialogShow" width="80%" :close-on-back="false" persistent>
        <v-form ref="cancelReqForm" class="admin-form app-form-input" validate-on="submit lazy"
            @submit.prevent="confirm">
            <v-card class="dialog-card">
                <v-toolbar density="compact">
                    <v-toolbar-title>{{ $t(header) }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-toolbar-items>
                        <v-btn icon="mdi-close" @click="cancel"></v-btn>
                    </v-toolbar-items>
                </v-toolbar>
                <v-divider class="mx-4"></v-divider>
                <v-card-text>
                    <div class="text-label">{{ $t('t-complaint-answer-back-to-user') }}</div>
                    <v-textarea v-model.trim="request.ascEndReason" class="hide-float-label" variant="outlined"
                        hide-details="auto" density="comfortable" :label="$t('t-please-input')"
                        :rows="isOpmRequest ? 10 : 15"
                        :rules="[rules.required(`${$t('t-please-fill')}${$t('t-complaint-cancel-reason')}`)]">
                    </v-textarea>
                    <v-row v-if="isOpmRequest">
                        <v-col cols="12 mb-0 pb-0 pt-6">
                            <div class="text-label">{{ $t('t-complaint-opm-report-attachments') }}</div>
                        </v-col>
                        <v-col cols="12" class="upload-section pt-0">
                            <v-btn variant="tonal" @click="displayUpload = true">
                                {{ $t('t-complaint-opm-add-file') }}
                            </v-btn>
                        </v-col>
                        <v-col v-if="attachments && attachments.length > 0" cols="12">
                            <v-data-table :headers="headers" :items="attachments" hide-default-footer
                                :no-data-text="$t('t-no-data-available')" :items-per-page="-1">
                                <template #[`item.index`]="{ index }">
                                    <div class="w-100 text-center">{{ index + 1 }}</div>
                                </template>
                                <template v-slot:item.actions="{ item, index }">
                                    <div class="actions d-flex align-center">
                                        <v-btn variant="text" color="primary" @click="fileSv.viewFileInEditMode(item)">
                                            <v-icon icon="mdi mdi-eye-outline" class="mr-1"></v-icon>
                                            <span>{{ $t('t-view-doc') }}</span>
                                        </v-btn>
                                        <v-btn variant="text" color="error" @click="deleteAttachment(item)">
                                            <v-icon icon="mdi mdi-close" class="mr-1"></v-icon>
                                            <span>{{ $t('t-delete') }}</span>
                                        </v-btn>
                                    </div>
                                </template>
                            </v-data-table>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-container class="mx-0">
                        <div class="d-flex justify-end">
                            <v-btn @click="cancel" variant="outlined" color="primary" size="large">
                                {{ $t(cancelText) }}
                            </v-btn>
                            <v-btn @click="confirm" variant="flat" color="primary" size="large">
                                {{ $t(confirmText) }}
                            </v-btn>
                        </div>
                    </v-container>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
    <Upload v-model="displayUpload" :attachments="attachments" @update:attachments="updateAttachments"></Upload>
    <!-- Fail -->
    <AlertDialog v-model="failAlert.display" :header="failAlert.header" :title="failAlert.messageTitle"
        :text="failAlert.messageText" :success="false">
    </AlertDialog>
</template>